import { useState } from "react";

// Flat garment measurements in cm (North American format)
const SIZES = ["S", "M", "L", "XL"];

// Source: factory spec DW-004
export const TEE_ROWS = [
  { label: "Body Length", note: "Collar to hem", values: [68, 74, 76, 78] },
  { label: "Chest Width", note: "Pit to pit", values: [58, 60, 62, 64] },
  { label: "Hem Width", note: "Across bottom", values: [55, 57, 59, 61] },
  { label: "Shoulder Width", note: "Seam to seam", values: [52, 54, 56, 58] },
];

// Source: factory spec DW-005 (M-XXL); S graded down one step per the spec's grading rule
export const HOODIE_ROWS = [
  { label: "Body Length", note: "Shoulder to hem", values: [68, 70, 72, 74] },
  { label: "Chest Width", note: "Pit to pit", values: [60, 62, 64, 66] },
  { label: "Hem Width", note: "Across bottom", values: [54, 56, 58, 60] },
  {
    label: "Shoulder Width",
    note: "Seam to seam",
    values: [47.5, 49.5, 51.5, 53.5],
  },
  {
    label: "Sleeve Length",
    note: "Shoulder to cuff",
    values: [58.5, 60, 61.5, 63],
  },
];

// Inches shown as whole numbers + reduced fractions to the nearest 1/8 (e.g. 21 3/8)
const toIn = (cm) => {
  const total = Math.round((cm / 2.54) * 8); // eighths
  const whole = Math.floor(total / 8);
  let num = total % 8;
  if (num === 0) return `${whole}`;
  let den = 8;
  while (num % 2 === 0) {
    num /= 2;
    den /= 2;
  }
  return `${whole ? `${whole} ` : ""}${num}/${den}`;
};

const unitBtn = (active) =>
  `px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
    active ? "bg-ink text-parchment" : "text-ink hover:text-oxblood"
  }`;

// Source: factory spec TSC203 (kids fitted polo); S/M/L = spec sizes 140/150/160
export const POLO_SIZES = ["S", "M", "L"];
export const POLO_ROWS = [
  {
    label: "Body Length",
    note: "Shoulder to hem",
    values: [53.5, 56.5, 59.5],
  },
  {
    label: "Chest Width",
    note: "Pit to pit",
    values: [41, 43, 45],
  },
  {
    label: "Hem Width",
    note: "Across bottom",
    values: [41, 43, 45],
  },
  {
    label: "Shoulder Width",
    note: "Seam to seam",
    values: [35, 36.5, 38],
  },
  {
    label: "Collar Width",
    note: "Across neck",
    values: [16, 16, 16],
  },
];

export const SizeChart = ({
  product,
  rows,
  footnote,
  sizes = SIZES,
  sizeLabel = "Size",
}) => {
  const [unit, setUnit] = useState("cm");
  const fmt = (v) => (unit === "cm" ? v : toIn(v));

  return (
    <section
      data-testid="size-chart-section"
      className="mt-24 border-t border-ink/10 pt-14"
    >
      <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
        <div>
          <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-mutedink mb-3">
            Size Guide
          </p>
          <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-ink">
            {product} sizing.
          </h2>
          <p className="mt-3 max-w-xl text-sm text-mutedink leading-relaxed">
            Garment measured flat. Lay a {product.toLowerCase()} that fits you
            well on a table and compare.
          </p>
        </div>
        <div
          data-testid="size-chart-unit-toggle"
          className="inline-flex border border-ink/20"
        >
          <button
            type="button"
            data-testid="size-chart-unit-cm"
            onClick={() => setUnit("cm")}
            className={unitBtn(unit === "cm")}
          >
            cm
          </button>
          <button
            type="button"
            data-testid="size-chart-unit-in"
            onClick={() => setUnit("in")}
            className={unitBtn(unit === "in")}
          >
            in
          </button>
        </div>
      </div>

      <div className="overflow-x-auto border border-ink/10 bg-white">
        <table data-testid="size-chart-table" className="w-full text-sm">
          <thead>
            <tr className="border-b border-ink/10">
              <th className="text-left px-5 py-4 font-mono text-[10px] uppercase tracking-[0.25em] text-mutedink font-normal">
                Measurement ({unit}) · {sizeLabel}
              </th>
              {sizes.map((s) => (
                <th
                  key={s}
                  className="px-5 py-4 font-serif text-lg text-ink font-normal"
                >
                  {s}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.label}
                data-testid={`size-row-${row.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="border-b border-ink/5 last:border-0"
              >
                <td className="px-5 py-4">
                  <p className="text-ink">{row.label}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-mutedink mt-0.5">
                    {row.note}
                  </p>
                </td>
                {row.values.map((v, i) => (
                  <td
                    key={sizes[i]}
                    className="px-5 py-4 text-center font-mono text-ink"
                  >
                    {fmt(v)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {footnote && (
        <p
          data-testid="size-chart-footnote"
          className="mt-4 font-mono text-[10px] uppercase tracking-[0.15em] text-mutedink"
        >
          {footnote}
        </p>
      )}
    </section>
  );
};
